import clsx from "clsx";
import { format, parseISO } from "date-fns";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";

import IconFacebook from "../../assets/icons/icon-facebook.svg";
import IconLinkedIn from "../../assets/icons/icon-linkedin.svg";
import IconTwitter from "../../assets/icons/icon-twitter.svg";

import RichText from "../rich-text";
import VideoSection from "../video-section";

import styles from "./article.module.scss";

const Header = ({ article }) => {
  return (
    <div className={styles.Header}>
      <h1>{article.title}</h1>
      <div className="mt-10">
        <AuthorFootnote article={article} />
      </div>
      <div>{format(parseISO(article.date), "MMMM d, Y")}</div>

      <SocialShareLinks article={article} />
    </div>
  );
};

const OverlayHeader = ({ article }) => {
  return (
    <div className={styles.OverlayHeader}>
      <div className="mt-10">
        <AuthorFootnote article={article} />
      </div>
      <div>
        <span className={styles.PublishedAt}>
          {format(parseISO(article.date), "MMMM d, Y")}
        </span>
      </div>

      <SocialShareLinks article={article} />
    </div>
  );
};

const AuthorFootnote = ({ className, article }) => {
  return (
    <div className={clsx(styles.AuthorFootnote, className)}>
      {article.author && <div>Text: {article.author}</div>}
      {article.illustrator && <div>Illustrations: {article.illustrator}</div>}
      {article.interviewer && <div>Interviewer: {article.interviewer}</div>}
      {article.photographer && <div>Photography: {article.photographer}</div>}
      {article.video && <div>Video: {article.video}</div>}
      {article.featuring && <div>Featuring: {article.featuring}</div>}
    </div>
  );
};

const FeaturedImage = ({ image, width, height, layout = "responsive" }) => {
  return (
    <div className={styles.FeaturedImage}>
      <figure>
        <Image
          src={`${image.url}?w=${width}&fm=webp&q=80`}
          alt={image.description}
          width={width}
          height={height}
          objectFit="cover"
          layout={layout}
        />
        {image.description && <figcaption>{image.description}</figcaption>}
      </figure>
    </div>
  );
};

const FeaturedImageWithOverlay = ({
  image,
  width,
  height,
  overlayText,
  layout = "responsive"
}) => {
  return (
    <div className={styles.FeaturedImage}>
      <figure>
        <Image
          src={`${image.url}?w=${width}&fm=webp&q=80`}
          alt={image.description}
          width={width}
          height={height}
          objectFit="cover"
          layout={layout}
        />
        <h1 className={styles.FeaturedImageOverlayText}>{overlayText}</h1>
        {image.description && <figcaption>{image.description}</figcaption>}
      </figure>
    </div>
  );
};

const FeaturedVideo = ({ video, poster }) => {
  return (
    <div className={styles.FeaturedVideo}>
      <video controls poster={poster && poster.url} preload="metadata">
        <source src={video.url} type="video/mp4" />
      </video>
    </div>
  );
};

const Content = ({ article }) => {
  return (
    <div className={styles.Content}>
      <RichText content={article.content} />
    </div>
  );
};

const SoundcloudEmbed = ({ embed }) => {
  return <div dangerouslySetInnerHTML={{ __html: embed }}></div>;
};

const SocialShareLinks = ({ className, article }) => {
  const [url, setUrl] = useState(null);

  useEffect(() => {
    setUrl(window.location);
  }, [setUrl]);

  return (
    <div className={clsx(styles.SocialShareLinks, className)}>
      <a
        className={clsx(styles.SocialShareLink)}
        href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(
          article.title
        )} - ${url}`}
        target="_blank"
        rel="noreferrer"
      >
        <IconTwitter role="presentation" />
      </a>
      <a
        className={clsx(styles.SocialShareLink)}
        href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`}
        target="_blank"
        rel="noreferrer"
      >
        <IconLinkedIn role="presentation" />
      </a>
      <a
        className={clsx(styles.SocialShareLink)}
        href={`https://www.facebook.com/sharer/sharer.php?u=${url}`}
        target="_blank"
        rel="noreferrer"
      >
        <IconFacebook role="presentation" />
      </a>
    </div>
  );
};

const ArticleLayout = ({ article }) => {
  return (
    <article className={styles.Article}>
      <Header article={article} />

      {/* No videos, just featured image. */}
      {!article.featuredVideo &&
        !article.cloudinaryVideo &&
        !article.youtubeEmbed &&
        article.featuredImage && (
          <FeaturedImage
            image={article.featuredImage}
            width="1240"
            height="886"
          />
        )}

      {article.featuredVideo && <VideoSection video={article.featuredVideo} />}

      {article.cloudinaryVideo && (
        <VideoSection video={{ url: article.cloudinaryVideo }} />
      )}

      {article.youtubeEmbed && (
        <VideoSection youtubeEmbed={article.youtubeEmbed} />
      )}

      <div className={styles.ArticleContainer}>
        {article.soundcloudEmbed && (
          <SoundcloudEmbed embed={article.soundcloudEmbed} />
        )}

        <Content article={article} />

        <div className="my-36">
          <AuthorFootnote className="text-center" article={article} />
        </div>
      </div>
    </article>
  );
};

const ArticleAlternativeLayout = ({ article }) => {
  // Filter the first set of content before first image.
  const firstImageIndex = useMemo(() => {
    if (!article.content) return null;
    return article.content.json.content.findIndex(
      (c) => c.nodeType === "embedded-asset-block"
    );
  }, [article]);

  const topContent = useMemo(() => {
    if (!article.content) return [];
    return article.content.json.content.slice(0, firstImageIndex);
  }, [article, firstImageIndex]);

  const restContent = useMemo(() => {
    if (!article.content) return [];
    return article.content.json.content.slice(firstImageIndex);
  }, [article, firstImageIndex]);

  const topArticle = {
    content: { json: { content: topContent } }
  };

  const restArticle = {
    content: { json: { content: restContent }, links: article.content?.links }
  };

  return (
    <article className={styles.Alternative}>
      <div className={styles.TwoColumn}>
        <div className={styles.HeaderColumn}>
          <Header article={article} />
        </div>
        <div className={styles.FeaturedImageColumn}>
          {!article.featuredVideo && article.featuredImage && (
            <FeaturedImage
              image={article.featuredImage}
              width="766"
              height="1106"
            />
          )}

          {article.featuredVideo && (
            <FeaturedVideo
              video={article.featuredVideo}
              poster={article.featuredImage}
            />
          )}

          {article.cloudinaryVideo && (
            <FeaturedVideo
              video={{ url: article.cloudinaryVideo }}
              poster={article.featuredImage}
            />
          )}

          {article.soundcloudEmbed && (
            <SoundcloudEmbed embed={article.soundcloudEmbed} />
          )}

          {topArticle && <Content article={topArticle} />}
        </div>
      </div>

      <div className="container">
        {restArticle && <Content article={restArticle} />}
      </div>

      <div className="my-36">
        <AuthorFootnote className="text-center" article={article} />
      </div>
    </article>
  );
};

const ArticleOverlayLayout = ({ article }) => {
  return (
    <article className={styles.OverlayArticle}>
      <FeaturedImageWithOverlay
        overlayText={article.title}
        image={article.featuredImage}
        width="1240"
        height="886"
      />

      <div className={styles.ArticleContainer}>
        <OverlayHeader article={article} />
      </div>

      <div className={styles.ArticleContainer}>
        {article.soundcloudEmbed && (
          <SoundcloudEmbed embed={article.soundcloudEmbed} />
        )}

        <Content article={article} />

        <div className="my-36">
          <AuthorFootnote className="text-center" article={article} />
        </div>
      </div>
    </article>
  );
};

const ArticleLayouts = {
  default: ArticleLayout,
  alternative: ArticleAlternativeLayout,
  overlay: ArticleOverlayLayout
};

const Article = ({ article }) => {
  const Layout = ArticleLayouts[article.template || "default"];

  return (
    <>
      <Layout article={article} />
    </>
  );
};

export default Article;
